import { clsx } from 'clsx'
import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { bandRange, formatShowableBand, isShowableBand } from '@/domain/results/band-display'
import { t, useLanguage } from '@/lib/i18n'
import { CountUp } from './count'
import { motionOn, useMove, type PlayProp } from './motion'
import { EASE_OUT, springEasing, SPRING_MS } from './springs'

/* ------------------------------------------------------------------ Meter ------------------------------------------------------------------ */

/**
 * The staff band meter (APP-DESIGN-DIRECTION 2.6, Tarabiyah's dense rows): nine 6 by 10px cells filled to the band in its
 * range colour, the half step as a half-filled cell, with the number beside it. Always left to right (it is a scale).
 */
export function Meter({ value, label, showNumber = true, className }: { value: number | null | undefined; label?: string; showNumber?: boolean; className?: string }) {
  useLanguage()
  if (!isShowableBand(value)) return <span className={clsx('r5-meter r5-meter--none', className)}>{t('No band yet')}</span>
  const range = bandRange(value)
  const text = formatShowableBand(value)!
  return (
    <span className={clsx('r5-meter', `r5-meter--${range}`, className)} role="img" aria-label={label ?? t('Band {0}', { 0: text })}>
      <span className="r5-meter__cells" aria-hidden="true">
        {Array.from({ length: 9 }, (_, index) => {
          const filled = value - index
          return <i key={index} className={clsx('r5-meter__cell', filled >= 1 && 'r5-meter__cell--on', filled > 0 && filled < 1 && 'r5-meter__cell--half')} />
        })}
      </span>
      {showNumber && <bdi className="num r5-meter__num" aria-hidden="true">{text}</bdi>}
    </span>
  )
}

/* ------------------------------------------------------------------ Week strip ------------------------------------------------------------------ */

export interface WeekDay {
  /** ISO date (yyyy-mm-dd), the key. */
  date: string
  /** Short day name, already in the interface language. */
  label: string
  state: 'done' | 'today' | 'today-done' | 'missed' | 'future'
}

/** Seven days ending today, from the completed-attempt dates the page already has. Pure. */
export function weekDays(practised: ReadonlySet<string>, today: Date, dayName: (date: Date) => string): WeekDay[] {
  const days: WeekDay[] = []
  for (let offset = 6; offset >= 0; offset -= 1) {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - offset)
    const key = isoDay(date)
    const done = practised.has(key)
    days.push({ date: key, label: dayName(date), state: offset === 0 ? (done ? 'today-done' : 'today') : done ? 'done' : 'missed' })
  }
  return days
}

export function isoDay(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Seven day rings (3.10.5): practised days draw their ring then flip to a check (120 ms apart), today is a partial arc with a
 * breathing dot until today has a completed attempt, missed days stay faint. No data, no strip: the caller decides.
 */
export function WeekStrip({ days, play = false, className }: { days: WeekDay[]; play?: PlayProp; className?: string }) {
  useLanguage()
  const ref = useRef<HTMLOListElement>(null)
  const go = useMove(play, ref)
  useLayoutEffect(() => {
    const root = ref.current
    if (!go || !root || !motionOn()) return
    root.querySelectorAll<HTMLElement>('.r5-week__day').forEach((day, index) => {
      const ring = day.querySelector<SVGCircleElement>('.r5-week__ring')
      const check = day.querySelector<SVGPathElement>('.r5-week__check')
      if (typeof ring?.animate !== 'function') return
      const delay = 200 + index * 120
      ring.animate([{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 400, delay, easing: EASE_OUT, fill: 'backwards' })
      check?.animate([{ opacity: 0, transform: 'scale(.4)' }, { opacity: 1, transform: 'none' }], { duration: SPRING_MS.pop, delay: delay + 360, easing: springEasing('pop'), fill: 'backwards' })
    })
  }, [go])
  return (
    <ol ref={ref} className={clsx('r5-week', className)} data-play={go ? 'true' : 'false'}>
      {days.map((day) => {
        const done = day.state === 'done' || day.state === 'today-done'
        const today = day.state === 'today' || day.state === 'today-done'
        const status = done ? t('Practised') : day.state === 'today' ? t('Not yet today') : t('No practice')
        return (
          <li key={day.date} className={clsx('r5-week__day', `r5-week__day--${day.state}`)} aria-label={`${day.label}: ${status}`}>
            <svg className="r5-week__svg" viewBox="0 0 28 28" aria-hidden="true" focusable="false">
              <circle className="r5-week__base" cx="14" cy="14" r="12" />
              {(done || day.state === 'today') && <circle className="r5-week__ring" cx="14" cy="14" r="12" pathLength={1} style={{ strokeDasharray: done ? '1 0' : '0.3 1' }} />}
              {done && <path className="r5-week__check" d="M9 14.4l3.4 3.3L19.2 10.6" />}
            </svg>
            {day.state === 'today' && <i className="r5-week__dot r5-breathe" aria-hidden="true" />}
            <span className={clsx('r5-week__label', today && 'r5-week__label--today')} aria-hidden="true">{day.label}</span>
          </li>
        )
      })}
    </ol>
  )
}

/* ------------------------------------------------------------------ Stat strip ------------------------------------------------------------------ */

export interface StatItem {
  key: string
  /** Label (11px caps). Already translated. */
  label: string
  /** A count to roll up, or null when the server did not send it. */
  value: number | null
  /** How the number is written ("3", "3.2 hours"). Defaults to the number. */
  format?: (value: number) => string
  /** When `value` is null: what to say instead ("Not reported"). */
  missing?: string
  /** Where the number comes from ("From your completed tests this week"). */
  caption?: ReactNode
  /** Count in this step (0.1 for hours). Default: integers. */
  decimals?: number
}

/**
 * Three (student) or four (staff) stats in one card divided by full-height hairlines (neutral dividers between siblings, not
 * stripes). Values count up on first view (700 ms `--r5-e-out`); the final value is in the DOM from the first frame.
 */
export function StatStrip({ items, play = false, className, label }: { items: StatItem[]; play?: PlayProp; className?: string; label?: string }) {
  useLanguage()
  const ref = useRef<HTMLDListElement>(null)
  const go = useMove(play, ref)
  return (
    <dl ref={ref} className={clsx('r5-stats', className)} aria-label={label} style={{ ['--r5-stats' as string]: items.length }}>
      {items.map((item, index) => {
        const scale = 10 ** (item.decimals ?? 0)
        return (
          <div key={item.key} className="r5-stats__item">
            <dt className="r5-stats__label">{item.label}</dt>
            <dd className="r5-stats__value">
              {item.value === null ? (
                <span className="r5-stats__missing">{item.missing ?? t('Not reported')}</span>
              ) : (
                <CountUp
                  value={Math.round(item.value * scale)}
                  play={go}
                  delay={index * 90}
                  duration={700}
                  format={(shown) => (item.format ?? String)(shown / scale)}
                />
              )}
            </dd>
            {item.caption && <dd className="r5-stats__caption">{item.caption}</dd>}
          </div>
        )
      })}
    </dl>
  )
}
